import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import Modal from '../Modals/Modal.js';
import './RelearnStyleButton.css';

const POLL_INTERVAL_MS = 2000;

export function describeJobItem(item) {
  if (item.status === 'queued') return 'Waiting its turn';
  if (item.status === 'processing') return 'Reading your notes and learning your style…';
  return item.detail || '';
}

function RelearnHero({ running = true, complete = false }) {
  return (
    <div
      className={`relearn-hero ${complete ? 'is-complete' : running ? 'is-running' : 'is-idle'}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" focusable="false">
        {complete ? (
          <path className="relearn-check" pathLength="1" d="m6 12 4 4 8-8" />
        ) : running ? (
          <>
            <circle className="relearn-ring" cx="12" cy="12" r="8" />
            <path className="relearn-spinner" d="M12 4a8 8 0 0 1 8 8" />
          </>
        ) : (
          <path d="M8 4h6l4 4v12H6V4h2m6 0v5h4M9 13h6m-6 3h6" />
        )}
      </svg>
    </div>
  );
}

function RelearnProgress({ job, error }) {
  if (error) {
    return (
      <div className="relearn-progress">
        <h3>Re-Learn My Style</h3>
        <p>{error}</p>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="relearn-progress">
        <div className="relearn-heading">
          <RelearnHero />
          <h3>Getting started…</h3>
        </div>
      </div>
    );
  }

  const item = job.items?.[0];
  const created = item?.status === 'succeeded';
  const running = job.status === 'running';

  return (
    <div className="relearn-progress">
      <div className="relearn-heading">
        <RelearnHero running={running} complete={!running && created} />
        <h3>{running ? 'Re-learning your style…' : created ? 'Your new template is ready' : 'No new template created'}</h3>
      </div>
      {item && (
        <div className="relearn-result">
          <div className="relearn-result-name">{created ? item.newName : item.name}</div>
          <p className="relearn-result-detail">{describeJobItem(item)}</p>
        </div>
      )}
      {running ? (
        <p className="relearn-footnote">
          You can close this window. We will keep going in the background.
        </p>
      ) : (
        <p className="relearn-footnote">
          {created
            ? 'Your new template is saved in Note Settings. Your previous template is unchanged.'
            : 'Your template is unchanged.'}
        </p>
      )}
    </div>
  );
}

function RelearnSectionPicker({ sections, selected, onChange, onStart, onCancel }) {
  const allSelected = selected.length === sections.length;
  return (
    <div className="relearn-picker">
      <h3>Choose sections to re-learn</h3>
      <p>Each section below has at least 3 completed note examples. Only selected sections will get new instructions.</p>
      <div className="relearn-picker-toolbar">
        <span>{selected.length} of {sections.length} selected</span>
        <button type="button" onClick={() => onChange(allSelected ? [] : sections)}>
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
      </div>
      <div className="relearn-picker-list">
        {sections.map((section) => (
          <label className="relearn-picker-row" key={section}>
            <input
              type="checkbox"
              checked={selected.includes(section)}
              onChange={() => onChange(
                selected.includes(section)
                  ? selected.filter((name) => name !== section)
                  : [...selected, section],
              )}
            />
            <span>{section}</span>
          </label>
        ))}
      </div>
      <div className="relearn-picker-actions">
        <Button color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="primary" disabled={selected.length === 0} onClick={onStart}>
          Create new template
        </Button>
      </div>
    </div>
  );
}

// Build a new copy of the opened template from matching completed notes.
const RelearnStyleButton = ({ configId }) => {
  const { apiClient } = useContext(GlobalContext);
  const [noteTypes, setNoteTypes] = useState(null);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [showSelection, setShowSelection] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [error, setError] = useState('');
  const watchedRun = useRef(false);

  const loadSummary = useCallback(
    () =>
      apiClient
        .getRelearnStyleSummary(configId)
        .then((response) => {
          setNoteTypes(response?.noteTypes || []);
          if (response?.job) setJob(response.job);
        })
        .catch(() => {
          setNoteTypes([]);
        }),
    [apiClient, configId],
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const running = job?.status === 'running';

  useEffect(() => {
    if (!running) return undefined;
    watchedRun.current = true;
    const timer = setInterval(() => {
      apiClient
        .getRelearnStyleStatus()
        .then((response) => {
          if (response?.job) setJob(response.job);
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [apiClient, running]);

  useEffect(() => {
    if (!running && watchedRun.current && job?.status === 'done') {
      watchedRun.current = false;
      loadSummary();
    }
  }, [running, job, loadSummary]);

  const ready = (noteTypes || []).some((noteType) => noteType.ready);

  const handleClick = async () => {
    if (running) {
      setShowSelection(false);
      setOpen(true);
      return;
    }
    setSelectedSections(noteTypes[0]?.readySections || []);
    setShowSelection(true);
    setError('');
    setOpen(true);
  };

  const handleStart = async () => {
    if (!selectedSections.length) return;
    setShowSelection(false);
    setJob(null);
    try {
      const response = await apiClient.startRelearnStyle(configId, selectedSections);
      if (response?.job) {
        setJob(response.job);
      } else {
        setError(response?.error || 'Could not start. Please try again.');
      }
    } catch (_) {
      setError('Could not start. Please try again.');
    }
  };

  if (!ready && !running) return null;

  return (
    <>
      <Button
        color="primary"
        size="sm"
        className="relearn-style-button"
        title={open ? undefined : 'Create a new version of this template from your notes that matches your style'}
        onClick={handleClick}
      >
        <i className="fa fa-magic mr-2" aria-hidden="true" />
        {running ? 'Re-Learning…' : 'Re-Learn My Style'}
      </Button>
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        isCloseButton={false}
        buttons={
          showSelection || running ? [] : [{ label: 'Done', color: 'primary', onClick: () => {} }]
        }
      >
        {showSelection ? (
          <RelearnSectionPicker
            sections={noteTypes[0]?.readySections || []}
            selected={selectedSections}
            onChange={setSelectedSections}
            onStart={handleStart}
            onCancel={() => setOpen(false)}
          />
        ) : (
          <RelearnProgress job={job} error={error} />
        )}
      </Modal>
    </>
  );
};

export default RelearnStyleButton;
